import React from 'react';
import PropTypes from 'prop-types';
import includes from 'lodash/includes';
import map from 'lodash/map';
import findIndex from 'lodash/findIndex';

import themeable from 'zignal-landscape/lib/themeable';
import Button from 'zignal-landscape/lib/Button';
import Card from 'zignal-landscape/lib/Card';
import Checkbox from 'zignal-landscape/lib/Checkbox';
import ChevronUpIcon from 'zignal-landscape/lib/Icon/ChevronUpIcon';
import DropdownIcon from 'zignal-landscape/lib/Icon/DropdownIcon';
import AddToGroupIcon from 'zignal-landscape/lib/Icon/AddToGroupIcon';
import DiscoverIcon from 'zignal-landscape/lib/Icon/DiscoverIcon';
import GearsIcon from 'zignal-landscape/lib/Icon/GearsIcon';
import TrimIcon from 'zignal-landscape/lib/Icon/TrimIcon';
import Remove1Icon from 'zignal-landscape/lib/Icon/Remove1Icon';
import Label from 'zignal-landscape/lib/Label';
import Menu from 'zignal-landscape/lib/Menu';

import MenuBulkActions from '../MenuBulkActions';
import {compareClusterNodes} from '../../dataUtils';

const CLUSTER_NODES_AMOUNT_TO_SHOW = 10;
class TopClustersByVolume extends React.Component {

  shouldComponentUpdate(nextProps) {
    return (
      this.props.clusterBy !== nextProps.clusterBy
      || this.props.isMenuButtonOpen !== nextProps.isMenuButtonOpen
      || this.props.selectedClusters.length !== nextProps.selectedClusters.length
      || compareClusterNodes(this.props.clusterNodes, nextProps.clusterNodes)
    );
  }

  render() {
    const {
      styles,
      clusterNodes,
      isMenuButtonOpen,
      isUserDiscoverFeatureEnabled,
      searchDiscover,
      selectClusters,
      selectedClusters,
      clearSelection,
      setClusterMenuButtonVisibility,
      setMenuBulkActionsRef,
      showSelectionOnly,
      toggleShowSelectionOnly,
      setAddToGroupModalVisibility,
      titleLabel,
      isSearchDiscoverDisabled,
      setGraphHoverFromList
    } = this.props;

    const actionButtonText = `${selectedClusters.length} ${titleLabel} Selected`;

    const handleTopClustersChange = (value) => {
      let payload = selectedClusters;
      const index = findIndex(payload, (item) => item === value);
      if (index === -1) {
        payload = [ ...selectedClusters, value ];
      } else {
        payload = [ ...selectedClusters.slice(0, index), ...selectedClusters.slice(index+1) ];
      }
      return selectClusters(payload);
    };
    const topClusterNodes = clusterNodes.slice(0, CLUSTER_NODES_AMOUNT_TO_SHOW);
    const clustersList = map(topClusterNodes, (clusterNode, index) => {
      const {id, label, avgAutomationScore } = clusterNode;
      const scoreUnknown = avgAutomationScore === 'Unknown';
      const isChecked = includes(selectedClusters, id);
      const onClickHandler = () => handleTopClustersChange(id);
      return (
        <li
          onMouseOver={() => setGraphHoverFromList(clusterNode)}
          key={id}
          className={styles.clusterElement}
        >
          <div className={styles.clusterIndexSection}>
            <Checkbox
              id={id}
              name={id}
              value={id}
              checked={isChecked}
            />
            <div className={styles.clusterIndex}>
              {index+1}
            </div>
          </div>
          <div className={styles.cluster}>{label}</div>
          <div className={styles.clusterAvgScore}>
            <p className={scoreUnknown ? {} : styles.availableScore}>
              <span className={styles.avgScore}>{avgAutomationScore}</span>
              {scoreUnknown ? null : (
                <span className={styles.iconScore}><GearsIcon width={10} height={10} /></span>
              )}
            </p>
          </div>
          <div className={styles.clusterCover} onClick={onClickHandler} />
        </li>
      );
    });

    const bulkActionsOptions = [{
      id: 'addToGroup',
      icon: <AddToGroupIcon />,
      text: ' Add to Group',
      onClick: () => setAddToGroupModalVisibility(true),
      cyData: 'action-add-to-group-domains-selected'
    }, {
      id: 'searchInDiscover',
      icon: <DiscoverIcon />,
      text: ' Search in Discover',
      onClick: () => searchDiscover(),
      disabled: !isUserDiscoverFeatureEnabled || isSearchDiscoverDisabled,
      cyData: 'action-search-in-discover-domains-selected'
    }, {
      id: 'showSelectionOnly',
      icon: <TrimIcon />,
      text: showSelectionOnly ? ' Show All' : ' Show Selection Only',
      onClick: () => toggleShowSelectionOnly(),
      cyData: 'action-show-options-domains-selected'
    }, {
      id: 'clearSelection',
      icon: <Remove1Icon />,
      text: ' Clear Selection',
      onClick: () => clearSelection(),
      cyData: 'action-clear-selection-domains-selected'
    }].filter(({disabled}) => !disabled);

    const menuBulkActions = isMenuButtonOpen && (
      <div ref={setMenuBulkActionsRef} className={styles.menuContainer}>
        <Menu>
          <MenuBulkActions bulkActionsOptions={bulkActionsOptions} />
        </Menu>
      </div>
    );

    return (
      <Card>
        <div className={styles.cardContainer}>
          <div>
            <Label size="large">Top {titleLabel} by volume:</Label>
          </div>
          <div className={styles.actionButtonContainer}>
            <div className={styles.actionButtonSection}>
              <Button
                data-cy="button-domains-selected-zii"
                onClick={() => setClusterMenuButtonVisibility(!isMenuButtonOpen)}
                buttonStyle="secondary"
                disabled={selectedClusters.length === 0}
              >
                {actionButtonText}
                <span className={styles.iconMargin}>
                  {isMenuButtonOpen ? <ChevronUpIcon /> : <DropdownIcon />}
                </span>
              </Button>
              {menuBulkActions}
            </div>
          </div>
          <ul data-cy="ul-domains-list-zii" onMouseLeave={() => setGraphHoverFromList(null)}>
            {clustersList}
          </ul>
        </div>
      </Card>
    );
  }

}

TopClustersByVolume.propTypes = {
  styles: PropTypes.object,
  clusterNodes: PropTypes.array,
  isMenuButtonOpen: PropTypes.bool,
  searchInDiscoverPattern: PropTypes.string,
  selectedClusters: PropTypes.array,
  clusterBy: PropTypes.string,
  showSelectionOnly: PropTypes.bool,
  titleLabel: PropTypes.string,
  isUserDiscoverFeatureEnabled: PropTypes.bool,
  isSearchDiscoverDisabled: PropTypes.bool,
  avgAutomationScore: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  // actions
  setMenuBulkActionsRef: PropTypes.func,
  toggleShowSelectionOnly: PropTypes.func,
  setClusterMenuButtonVisibility: PropTypes.func,
  setAddToGroupModalVisibility: PropTypes.func,
  selectClusters: PropTypes.func,
  clearSelection: PropTypes.func,
  searchDiscover: PropTypes.func,
  setGraphHoverFromList: PropTypes.func
};

TopClustersByVolume.defaultProps = {
  clusterNodes: []
};

export default themeable(TopClustersByVolume);
